export const SUPPORTED_LOCALES = ['en', 'he', 'ar', 'es'] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: SupportedLocale = 'en';

export const RTL_LOCALES: SupportedLocale[] = ['he', 'ar'];

export const LOCALE_COOKIE = 'NEXT_LOCALE';

export const LOCALE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

export const LOCALE_NATIVE_NAMES: Record<SupportedLocale, string> = {
  en: 'English',
  he: 'עברית',
  ar: 'العربية',
  es: 'Español',
};

export const isSupportedLocale = (value?: string | null): value is SupportedLocale =>
  !!value && (SUPPORTED_LOCALES as readonly string[]).includes(value);

export const getDirection = (locale: string): 'ltr' | 'rtl' =>
  RTL_LOCALES.includes(locale as SupportedLocale) ? 'rtl' : 'ltr';

// Latin numbering is forced for ar so prices/dates keep Western digits.
export const INTL_LOCALES: Record<SupportedLocale, string> = {
  en: 'en-US',
  he: 'he-IL',
  ar: 'ar-EG-u-nu-latn',
  es: 'es-ES',
};

export const DAYJS_LOCALES: Record<SupportedLocale, string> = {
  en: 'en',
  he: 'he',
  ar: 'ar',
  es: 'es',
};

// Country → locale mapping for the geo-IP fallback (only used when the
// browser reports no supported language at all).
const ARABIC_COUNTRIES = [
  'AE',
  'BH',
  'DJ',
  'DZ',
  'EG',
  'IQ',
  'JO',
  'KM',
  'KW',
  'LB',
  'LY',
  'MA',
  'MR',
  'OM',
  'PS',
  'QA',
  'SA',
  'SD',
  'SO',
  'SY',
  'TN',
  'YE',
];
const SPANISH_COUNTRIES = [
  'AR',
  'BO',
  'CL',
  'CO',
  'CR',
  'CU',
  'DO',
  'EC',
  'ES',
  'GQ',
  'GT',
  'HN',
  'MX',
  'NI',
  'PA',
  'PE',
  'PR',
  'PY',
  'SV',
  'UY',
  'VE',
];

export const countryToLocale = (countryCode?: string | null): SupportedLocale => {
  const code = (countryCode || '').toUpperCase();
  if (code === 'IL') return 'he';
  if (ARABIC_COUNTRIES.includes(code)) return 'ar';
  if (SPANISH_COUNTRIES.includes(code)) return 'es';
  return DEFAULT_LOCALE;
};
